import { ItemIdentifier, ItemType, Action } from "~/parse/sfml-ast";
import { ViewportObserver } from "../viewport";
import { SFMLCullingElement, SFMLImpressionable } from "~/renderer/web-components/element";
import { ImageLoader, ImageLoaderCallback } from "./util";
import { FlyerImageAnchor } from "./flyter-image-anchor";

const MAX_CANVAS_SIZE = 2290 * 2289;
const TILE_NUDGE = 2;

export type LinkProvider = (itemData: ItemIdentifier) => string;

function findPreferredResolution(zoomScale: number, resolutions: number[]) {
  let minDelta = Number.POSITIVE_INFINITY;
  let bestResolution = 0;
  for (let i = 0, n = resolutions.length; i < n; ++i) {
    const delta = Math.abs(resolutions[i] * zoomScale - 1);
    if (delta < minDelta) {
      bestResolution = i;
      minDelta = delta;
    }
  }

  return bestResolution;
}

export class FlyerImage extends SFMLCullingElement implements SFMLImpressionable {
  private readonly slotContainer: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private static assetUrl = "https://f.wishabi.net/";
  private static linkProvider: LinkProvider | null = null;

  private lastUserZoom: number | null = null;
  private lastDevicePixelRatio: number | null = null;

  private requested = new Map<string, boolean>();
  private cancellations: { path: string; callback: ImageLoaderCallback }[] = [];

  constructor() {
    super();

    this.canvas = document.createElement("canvas");
    this.appendChild(this.canvas);

    const context = this.canvas.getContext("2d");
    if (!context) throw "Bad rendering context";
    this.context = context;

    this.slotContainer = document.createElement("div");
    this.appendChild(this.slotContainer);

    this.setAttribute("impressionable", "");
  }

  static observedAttributes = ["src-rect"];

  protected attributeChangedCallback(attr: string, oldValue: string | null, newValue: string | null) {
    this.invalidate();
  }

  public handleResize() {
    super.handleResize();

    const userZoom = Math.round((document.documentElement.clientWidth / ViewportObserver.shared.viewWidth) * 100) / 100;

    if (
      this.canvas.width !== 0 &&
      this.lastUserZoom === userZoom &&
      this.lastDevicePixelRatio === window.devicePixelRatio
    )
      return;

    this.lastUserZoom = userZoom;
    this.lastDevicePixelRatio = window.devicePixelRatio;

    this.resizeCanvas(
      userZoom * this.offsetWidth * window.devicePixelRatio,
      userZoom * this.offsetHeight * window.devicePixelRatio,
    );

    this.invalidate();
  }

  public static setAssetUrl(url: string) {
    FlyerImage.assetUrl = url;
  }

  public static setLinkProvider(linkProvider: LinkProvider) {
    FlyerImage.linkProvider = linkProvider;
  }

  private resizeCanvas(newWidth: number, newHeight: number) {
    if (newWidth * newHeight > MAX_CANVAS_SIZE) {
      const shrink = MAX_CANVAS_SIZE / (newWidth * newHeight);
      newWidth *= shrink;
      newHeight *= shrink;
    }

    const buffer = document.createElement("canvas");
    const bufferContext = buffer.getContext("2d");

    if (!bufferContext) return;

    if (newWidth === this.canvas.width && newHeight === this.canvas.height) return;

    buffer.width = this.canvas.width;
    buffer.height = this.canvas.height;

    // Draw iff the canvas has valid dimensions
    if (this.canvas.height > 0 && this.canvas.width > 0) {
      bufferContext.drawImage(this.canvas, 0, 0, buffer.width, buffer.height);
    }

    this.canvas.width = newWidth;
    this.canvas.height = newHeight;

    const context = this.canvas.getContext("2d");

    if (!context) return;

    if (this.canvas.height > 0 && this.canvas.width > 0 && buffer.height > 0 && buffer.width > 0) {
      context.drawImage(buffer, 0, 0, newWidth, newHeight);
    }
  }

  public handleVisible() {
    super.handleVisible();
    this.handleResize();
    this.invalidate();
  }

  protected handleHidden() {
    super.handleHidden();
    this.cancelImageLoading();
    this.invalidate();
    this.requested.clear();
  }

  get path(): string | null {
    return this.getAttribute("path");
  }

  set path(value) {
    if (value) this.setAttribute("path", value);
    else this.removeAttribute("path");
  }

  get width(): string | null {
    return this.getAttribute("width");
  }

  set width(value) {
    if (value) this.setAttribute("width", value);
    else this.removeAttribute("width");
  }

  get height(): string | null {
    return this.getAttribute("height");
  }

  set height(value) {
    if (value) this.setAttribute("height", value);
    else this.removeAttribute("height");
  }

  get resolutions(): string | null {
    return this.getAttribute("resolutions");
  }

  set resolutions(value) {
    if (value) this.setAttribute("resolutions", value);
    else this.removeAttribute("resolutions");
  }

  get srcRect(): string | null {
    return this.getAttribute("src-rect");
  }

  set srcRect(value) {
    if (value) this.setAttribute("src-rect", value);
    else this.removeAttribute("src-rect");
  }

  get srcRectNumbers(): number[] {
    const srcRectString = this.srcRect;
    if (!srcRectString) return [0, 0, 0, 0];

    const srcRectStringParts = srcRectString.split(" ");
    const srcLeft = Number(srcRectStringParts[0]);
    const srcTop = Number(srcRectStringParts[1]);
    const srcWidth = Number(srcRectStringParts[2]);
    const srcHeight = Number(srcRectStringParts[3]);
    return [srcLeft, srcTop, srcWidth, srcHeight];
  }

  requestAnimationFrameToken: number | null = null;

  private invalidate() {
    if (this.requestAnimationFrameToken === null) {
      this.requestAnimationFrameToken = window.requestAnimationFrame(this.handleRequestAnimationFrame);
    }
  }

  private handleRequestAnimationFrame = () => {
    this.requestAnimationFrameToken = null;
    this.draw();
  };

  private cancelImageLoading() {
    for (let cancel of this.cancellations) {
      ImageLoader.shared.cancelRequest(cancel.path, cancel.callback);
    }

    this.cancellations = [];
  }

  private removeCancellation(path: string, callback: ImageLoaderCallback) {
    this.requested.set(path, false);

    for (let i = 0, n = this.cancellations.length; i < n; ++i) {
      const cancel = this.cancellations[i];
      if (cancel.path === path && cancel.callback === callback) {
        this.cancellations.splice(i, 1);
      }

      return;
    }
  }

  draw() {
    if (!this.visible) {
      this.canvas.width = 0;
      this.canvas.height = 0;
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      return;
    }

    const path = this.path;
    if (!path) return;

    const resolutionsString = this.resolutions;
    if (!resolutionsString) return;

    const stringParts = resolutionsString.split(" ");
    if (stringParts.length === 0) return;
    const resolutions = stringParts.map((x) => Number(x));

    const srcRectString = this.srcRect;
    if (!srcRectString) return;

    const srcRectStringParts = srcRectString.split(" ");
    const srcLeft = Number(srcRectStringParts[0]);
    const srcTop = Number(srcRectStringParts[1]);
    const srcWidth = Number(srcRectStringParts[2]);
    const srcHeight = Number(srcRectStringParts[3]);

    const screenScaleX = this.canvas.offsetWidth / srcWidth;
    const screenScaleY = this.canvas.offsetHeight / srcHeight;

    const scaleX = this.canvas.width / srcWidth;
    const scaleY = this.canvas.height / srcHeight;
    const scale = Math.min(scaleX, scaleY);
    const resolution = findPreferredResolution(scale, resolutions);

    this.context.save();
    this.context.scale(scaleX, scaleY);
    this.context.translate(-srcLeft, -srcTop);

    const flyerWidth = Number(this.width) || 0;
    const flyerHeight = Number(this.height) || 0;

    const tileSize = 256 * resolutions[resolution];
    const totalCols = Math.ceil(flyerWidth / tileSize);
    const totalRows = Math.ceil(flyerHeight / tileSize);
    const topOffset = Math.ceil(totalRows * tileSize - flyerHeight);

    const tilesLeft = Math.max(0, Math.floor((srcLeft - TILE_NUDGE) / tileSize));
    const tilesTop = Math.max(0, Math.floor(srcTop / tileSize));
    const tilesRight = Math.min(totalCols - 1, Math.floor((srcLeft + srcWidth + TILE_NUDGE) / tileSize));
    const tilesBottom = Math.min(totalRows - 1, Math.floor((srcTop + srcHeight + topOffset) / tileSize));

    for (let y = tilesTop; y <= tilesBottom; y++) {
      const worldY = y * tileSize - topOffset;

      for (let x = tilesLeft; x <= tilesRight; x++) {
        const worldX = x * tileSize;
        const tilePath = `${FlyerImage.assetUrl}${path}${resolution}_${x}_${totalRows - y - 1}.jpg`;

        const forceRedraw = !this.requested.get(tilePath);
        const callback = () => {
          this.removeCancellation(tilePath, callback);

          if (forceRedraw) this.invalidate();
        };

        this.requested.set(tilePath, true);
        this.cancellations.push({ path: tilePath, callback });

        const img = ImageLoader.shared.getImage(tilePath, callback);
        if (!img) {
          continue;
        }

        this.context.drawImage(img, 0, 0, 256, 256, worldX, worldY, tileSize, tileSize);
      }
    }
    this.context.restore();

    if (this.slotContainer.innerHTML.length > 0) {
      this.getAreas().forEach((area) => {
        area.positionInParent(area.getScreenRect([srcLeft, srcTop, srcWidth, srcHeight], screenScaleX, screenScaleY));
      });
      return;
    }

    const areas = Array.from(this.querySelectorAll("sfml-flyer-image-a"));

    for (let i = 0, n = areas.length; i < n; ++i) {
      const area = areas[i];
      if (area instanceof FlyerImageAnchor) {
        const srcRect = area.getAttribute("src-rect");
        const href = area.getAttribute("href");
        if (srcRect) {
          const a = document.createElement("a");

          const itemType = area.getAttribute("item-type") as ItemType;
          const itemId = area.getAttribute("source-id") as string;

          if (FlyerImage.linkProvider && itemType && itemId)
            a.href = FlyerImage.linkProvider({
              itemType: itemType,
              itemId: itemId,
            });
          else a.href = href || "#";

          a.dataset.itemId = itemId;
          a.dataset.itemType = itemType;

          const label = area.getAttribute("label");
          if (label) a.setAttribute("aria-label", label);
          a.style.display = "block";
          a.style.position = "absolute";
          area.anchor = a;
          area.positionInParent(area.getScreenRect([srcLeft, srcTop, srcWidth, srcHeight], screenScaleX, screenScaleY));

          this.slotContainer.appendChild(a);
        }
      }
    }
  }

  public getAreas(): FlyerImageAnchor[] {
    const areas = this.querySelectorAll("sfml-flyer-image-a");
    const flyerImageAnchors: FlyerImageAnchor[] = [];
    for (let i = 0, n = areas.length; i < n; ++i) {
      const area = areas[i];

      if (area instanceof FlyerImageAnchor) flyerImageAnchors.push(area);
    }

    return flyerImageAnchors;
  }

  public clientRects(): { identifier: ItemIdentifier; rect: ClientRect }[] {
    const areas = this.querySelectorAll("sfml-flyer-image-a");

    const rects: { identifier: ItemIdentifier; rect: ClientRect }[] = [];
    for (let i = 0, n = areas.length; i < n; ++i) {
      const area = areas[i];

      if (!(area instanceof FlyerImageAnchor) || !area.anchor) continue;

      if (area.itemType === null) continue;

      if (area.itemId === null) continue;

      const rect = ViewportObserver.shared.getBoundingClientRect(area.anchor);

      const identifier = {
        itemType: area.itemType,
        itemId: area.itemId,
      };
      rects.push({ identifier, rect });
    }

    return rects;
  }
}
customElements.define("sfml-flyer-image", FlyerImage);



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/renderer/web-components/image/flyer-image.ts